import type React from 'react';
import type { MouseEvent } from 'react';

import { Button } from '@change/ds-core-ui';
import { ChevronLeftIcon, ChevronRightIcon } from '@change/ds-icons';

import { useI18n } from '@change-corgi/core-react/i18n';

import { useCategoryPagination } from './hooks';

/**
 * Every control here is a real anchor (`Button as="a" href=...`) rather than a `button` with an
 * `onPageChange` handler — a JS-less crawler must be able to walk page 1 through page N by
 * following `href`s alone. The `a` primitive is registered to `DSLink` app-wide (see
 * `src/shared/ds/context.tsx`), so these still navigate client-side; wrapping them in the
 * router's own `Link` would be redundant and is blocked by the `no-as-router-link` lint rule.
 * See {@link useCategoryPagination}.
 */
export const CategoryPagination: React.FC<EmptyProps> = () => {
	const { translate } = useI18n();
	const {
		data: { totalPages, items, previous, next },
		actions: { handlePageClick },
	} = useCategoryPagination();

	/**
	 * The design system types `Button`'s `onClick` as react-aria's `PressEvent`, but when it renders
	 * as an anchor it forwards the handler straight to the DOM instead of going through react-aria
	 * (confirmed both in `@change/ds-core-ui`'s `Button` and by observing a real click), so what
	 * arrives is a `MouseEvent`. Narrowed once here rather than at each of the three call sites.
	 */
	const onPageClick = (page: number) => (event: unknown) =>
		handlePageClick(page, event as MouseEvent<HTMLAnchorElement>);

	if (totalPages <= 1) {
		return null;
	}

	return (
		<nav aria-label={translate('corgi.petition_guides_category_page.pagination.aria_label')}>
			<ol className="flex flex-wrap items-center justify-center gap-2">
				{previous && (
					<li>
						<Button
							as="a"
							href={previous.href}
							variant="tertiary"
							size="small"
							leadingIcon={<ChevronLeftIcon />}
							onClick={onPageClick(previous.page)}
							data-qa="pagination-previous"
						>
							{translate('corgi.components.pagination.previous')}
						</Button>
					</li>
				)}

				{items.map((item, index) =>
					item.type === 'ellipsis' ? (
						<li key={`ellipsis-${index}`} aria-hidden="true" className="text-typography-weak px-2">
							&hellip;
						</li>
					) : (
						<li key={item.page}>
							<Button
								as="a"
								href={item.href}
								variant={item.current ? 'primary' : 'tertiary'}
								size="small"
								aria-current={item.current ? 'page' : undefined}
								aria-label={translate('corgi.petition_guides_category_page.pagination.page_aria_label', {
									page: item.page,
								})}
								onClick={onPageClick(item.page)}
								data-qa={`pagination-page-${item.page}`}
							>
								{item.page}
							</Button>
						</li>
					),
				)}

				{next && (
					<li>
						<Button
							as="a"
							href={next.href}
							variant="tertiary"
							size="small"
							trailingIcon={<ChevronRightIcon />}
							onClick={onPageClick(next.page)}
							data-qa="pagination-next"
						>
							{translate('corgi.components.pagination.next')}
						</Button>
					</li>
				)}
			</ol>
		</nav>
	);
};
